import {
  Collection,
  CountableApiResponse,
  FileType,
  SerializedFile,
  SerializedFilter,
  SerializedMeter,
  SerializedMeterByView,
  ViewMeters,
} from '@enmon/commons/lib/enmon';
import { BaseApiClient } from './baseApiClient';
import { Authenticator } from '../httpClient';

export interface Pagination {
  page?: number;
  pageSize?: number;
}

export interface FilesFilter extends Pagination {
  invoices?: string[];
  manualReadings?: string[];
  emissionRecords?: string[];
  customEmissionFactorValidities?: string[];
  customEmissionFactors?: string[];
  meters?: string[];
  supplyPoints?: string[];
  customers?: string[];
  establishments?: string[];
  floorPlans?: string[];
  collections?: Collection[];
  revisions?: string[];
  fileTypes?: FileType[];
}

type DecodedMessage = {
  registerBy: { channel: string } | object;
  measurement: { date: string; value: number };
  type: string;
};

export type DecoderFilter = Pick<SerializedFilter, 'dateTo' | 'dateFrom' | 'search' | 'page' | 'pageSize' | 'toolsIds'>;

export type GetMeterArg = { id: string; filter?: SerializedFilter; signal?: AbortSignal };
export type GetMeterResult = SerializedMeter;

export type GetMetersArg = { filter?: SerializedFilter; view?: ViewMeters; signal?: AbortSignal };
export type GetMetersResult<TView extends ViewMeters = ViewMeters.Default> = CountableApiResponse<
  SerializedMeterByView[TView][]
>;

export type GetFileLinkArg = SerializedFile['_id'];
export type GetFileLinkResponse = { link: string } | null;

export type GetFilesArg = FilesFilter;
export type GetFilesResponse = CountableApiResponse<SerializedFile[]> | null;

export class EnmonToolsApiClient extends BaseApiClient {
  constructor(authenticator: Authenticator | undefined) {
    super('enmon-tools', authenticator);
  }

  public async getDecodedMessages<R = DecodedMessage>(
    params: DecoderFilter,
    signal: AbortSignal | undefined,
  ): Promise<R[]> {
    return await this.http.get<R[]>('/decoder', {
      params,
      signal,
    });
  }

  public async getMeter<R = GetMeterResult>({ id, filter, signal }: GetMeterArg): Promise<R> {
    return await this.http.get<R>(`/meters/${id}`, {
      params: filter,
      signal,
    });
  }

  public async getMeters<R = GetMetersResult<ViewMeters.Default>>({
    filter,
    view = ViewMeters.Default,
    signal,
  }: GetMetersArg): Promise<CountableApiResponse<R[]>> {
    return await this.http.get<CountableApiResponse<R[]>>('/meters', {
      params: { ...filter, view },
      signal,
    });
  }

  public async getFiles(filter: GetFilesArg, signal?: AbortSignal): Promise<GetFilesResponse> {
    return await this.http.get<GetFilesResponse>(`/files`, {
      params: filter,
      signal,
    });
  }

  public async getFileLink(fileId: GetFileLinkArg, signal?: AbortSignal): Promise<GetFileLinkResponse> {
    return await this.http.get<GetFileLinkResponse>(`/files/${fileId}/link`, {
      signal,
    });
  }
}
